/**
 * Dashboard Mapper - Backend to Frontend
 * @description Maps backend dashboard data (shortened fields) to frontend dashboard data (full fields)
 */

import {
  BackendDashboardLecture,
  BackendDashboardOperation,
  BackendDevice,
} from '@/types/backend.types';
import { DashboardLecture, DashboardOperation, DashboardDevice } from '@/types/dashboard.types';

export class DashboardMapper {
  /**
   * Map single backend dashboard lecture to frontend dashboard lecture
   */
  static lectureToFrontend(backendLecture: BackendDashboardLecture): DashboardLecture {
    const output = {
      id: backendLecture.id,
      lectureIdentifier: backendLecture.tag,
      deviceId: backendLecture.devId,
      operationId: backendLecture.opId,
      timestamp: backendLecture.ts,
      tz: backendLecture.tz,
      coordinates: {
        latitude: backendLecture.coords.lat,
        longitude: backendLecture.coords.long,
      },
      eventType: backendLecture.type,
      inclinationAngle: {
        lateral: backendLecture.ang.lat,
        longitudinal: backendLecture.ang.long,
      },
      angleLimits: {
        lateral: backendLecture.lim.lat,
        longitudinal: backendLecture.lim.long,
      },
      sequence: backendLecture.seq,
    };
    return output;
  }

  /**
   * Map array of backend dashboard lectures to frontend dashboard lectures
   */
  static lectureToFrontendArray(backendLectures: BackendDashboardLecture[]): DashboardLecture[] {
    return backendLectures.map((lecture) => DashboardMapper.lectureToFrontend(lecture));
  }

  /**
   * Map single backend dashboard operation to frontend dashboard operation
   */
  static operationToFrontend(backendOperation: BackendDashboardOperation): DashboardOperation {
    return {
      id: backendOperation.id,
      operationIdentifier: backendOperation.tag,
      deviceId: backendOperation.devId,
      startTimestamp: backendOperation.start,
      endTimestamp: backendOperation.end,
      durationMinutes: backendOperation.dur,
      performance: undefined, // Not available in backend DTO
      centroidLocation: backendOperation.centroid
        ? {
            latitude: backendOperation.centroid.lat,
            longitude: backendOperation.centroid.long,
          }
        : undefined,
      centroid_latitutde: backendOperation.centroid?.lat,
      centroid_longitude: backendOperation.centroid?.long,
      angleLimits: undefined, // Not available in backend DTO
      weatherSummary: undefined, // Not available in backend DTO
      metadata: undefined, // Not available in backend DTO
      // Aggregated data for dashboard
      lectureCount: backendOperation.lectureCount,
      isActive: backendOperation.isActive,
      // Computed fields from backend
      durationFormatted: backendOperation.durationFormatted,
      timeRangeFormatted: backendOperation.timeRangeFormatted,
      tz: backendOperation.tz,
    };
  }

  /**
   * Map array of backend dashboard operations to frontend dashboard operations
   */
  static operationToFrontendArray(
    backendOperations: BackendDashboardOperation[]
  ): DashboardOperation[] {
    return backendOperations.map((operation) => DashboardMapper.operationToFrontend(operation));
  }

  /**
   * Map single backend device to frontend dashboard device
   */
  static deviceToFrontend(backendDevice: BackendDevice): DashboardDevice {
    return {
      id: backendDevice.id,
      serialNumber: backendDevice.serialNumber,
      plate: backendDevice.plate,
      alias: backendDevice.alias,
      type: backendDevice.type,
      status: backendDevice.status,
      lastConnection: backendDevice.lastConnection,
      lastOperation: backendDevice.lastOperation,
      lastConnectionTz: backendDevice.lastConnectionTz,
      lastOperationTz: backendDevice.lastOperationTz,
      operationCount: backendDevice.operationCount,
    };
  }

  /**
   * Map array of backend devices to frontend dashboard devices
   */
  static deviceToFrontendArray(backendDevices: BackendDevice[]): DashboardDevice[] {
    return backendDevices.map((device) => DashboardMapper.deviceToFrontend(device));
  }
}
